import React, { useEffect, useState } from 'react';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import CustomTable from '../../common/CustomTable';
import CreateUserDialog from './CreateUserDialog';
import apiService from '../../../services/ApiService';
import { apiRoutes } from '../../../shared/apiRoutes';
import { useAuthorization } from '../../../auth/useAuthorization';
import { Role } from '../../../auth/roles';
import { USER_CREATE, USER_DELETE, USER_EDIT } from '../../../auth/permissions';
import { formatDateTimeIST } from '../../../utils/util';

interface User {
  id: number;
  firstName: string;
  lastName: string;
  userName: string;
  role: string;
  phoneNumber: string;
  partyId?: number | null;
  partyCode?: string;
  partyName?: string;
  twoFactorEnabled?: boolean;
  modifiedOn: string;
}

// API response might have different field names, so we'll map them
interface ApiUser {
  id: number;
  firstName?: string;
  lastName?: string;
  userName?: string;
  username?: string;
  role?: string;
  phoneNumber?: string;
  phone?: string;
  partyId?: number | null;
  PartyId?: number | null;
  partyCode?: string;
  PartyCode?: string;
  partyName?: string;
  PartyName?: string;
  modifiedOn?: string;
  modifiedAt?: string;
  updatedAt?: string;
  [key: string]: any; // Allow for additional fields
}

// Paginated API response structure
interface PaginatedResponse {
  data?: ApiUser[];
  items?: ApiUser[];
  totalCount?: number;
  total?: number;
  page?: number;
  pageSize?: number;
  hasMore?: boolean;
}

const Users: React.FC = () => {
  const { hasPermission, hasRole } = useAuthorization();
  const canCreateUser = hasPermission(USER_CREATE);
  const canEditUser = hasPermission(USER_EDIT);
  const canDeleteUser = hasPermission(USER_DELETE);
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resetUser, setResetUser] = useState<User | null>(null);
  const [resetting, setResetting] = useState(false);
  const [filters, setFilters] = useState({ role: '', phoneNumber: '' });

  useEffect(() => {
    fetchUsers();
    
    const handleUserListRefresh = () => {
      fetchUsers();
    };
    
    window.addEventListener('user-created', handleUserListRefresh);
    window.addEventListener('user-updated', handleUserListRefresh);
    
    return () => {
      window.removeEventListener('user-created', handleUserListRefresh);
      window.removeEventListener('user-updated', handleUserListRefresh);
    };
  }, []);

  const mapApiUserToUser = (apiUser: ApiUser): User => {
    const partyId = apiUser.partyId ?? apiUser.PartyId ?? null;
    const partyCode = apiUser.partyCode || apiUser.PartyCode || '';
    const partyName = apiUser.partyName || apiUser.PartyName || '';
    return {
      id: apiUser.id ?? apiUser.Id ?? 0,
      firstName: apiUser.firstName || apiUser.FirstName || '',
      lastName: apiUser.lastName || apiUser.LastName || '',
      userName: apiUser.userName || apiUser.username || apiUser.UserName || apiUser.Username || '',
      role: apiUser.role || apiUser.Role || '',
      phoneNumber: apiUser.phoneNumber || apiUser.phone || '',
      partyId,
      partyCode,
      partyName,
      twoFactorEnabled: Boolean(apiUser.twoFactorEnabled ?? apiUser.TwoFactorEnabled),
      modifiedOn: apiUser.modifiedOn || apiUser.modifiedAt || apiUser.updatedAt || new Date().toISOString(),
    };
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Request a large pageSize to get all users, or use query parameters if API supports it
      const response = await apiService.get<ApiUser[] | PaginatedResponse>(
        `${apiRoutes.users.list}?pageSize=1000&page=1`
      ); // Debug log
      
      // Handle different response structures
      let usersData: ApiUser[] = [];
      let totalCount = 0;
      
      if (Array.isArray(response)) {
        // Direct array response
        usersData = response;
        totalCount = response.length;
      } else {
        const paginatedResponse = response as PaginatedResponse;
        
        // Check for data array
        if (paginatedResponse.data && Array.isArray(paginatedResponse.data)) {
          usersData = paginatedResponse.data;
          totalCount = paginatedResponse.totalCount || paginatedResponse.total || paginatedResponse.data.length;
        } 
        // Check for items array
        else if (paginatedResponse.items && Array.isArray(paginatedResponse.items)) {
          usersData = paginatedResponse.items;
          totalCount = paginatedResponse.totalCount || paginatedResponse.total || paginatedResponse.items.length;
        } // Debug log
        
        // If we got fewer records than total, try to fetch all pages
        if (totalCount > usersData.length && paginatedResponse.hasMore !== false) {
          // Try fetching with even larger pageSize or fetch remaining pages
          const allUsers: ApiUser[] = [...usersData];
          let currentPage = 2;
          const pageSize = 1000;
          
          while (allUsers.length < totalCount) {
            try {
              const nextPageResponse = await apiService.get<ApiUser[] | PaginatedResponse>(
                `${apiRoutes.users.list}?pageSize=${pageSize}&page=${currentPage}`
              );
              
              let nextPageData: ApiUser[] = [];
              if (Array.isArray(nextPageResponse)) {
                nextPageData = nextPageResponse;
              } else {
                const nextPaginated = nextPageResponse as PaginatedResponse;
                nextPageData = nextPaginated.data || nextPaginated.items || [];
              }
              
              if (nextPageData.length === 0) break;
              
              allUsers.push(...nextPageData);
              currentPage++;
              
              // Safety check to prevent infinite loops
              if (currentPage > 100) break;
            } catch (err) {
              console.warn('Error fetching additional pages:', err);
              break;
            }
          }
          
          usersData = allUsers; // Debug log
        }
      }

      const mappedUsers = usersData.map(mapApiUserToUser); // Debug log
      setUsers(mappedUsers);
      applyFilters(mappedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      window.dispatchEvent(new CustomEvent('show-toast', { 
        detail: { message: 'Failed to load users', type: 'error' } 
      }));
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (data: User[]) => {
    let filtered = [...data];

    if (filters.role) {
      filtered = filtered.filter(u =>
        (u.role || '').toLowerCase().includes(filters.role.toLowerCase())
      );
    }

    if (filters.phoneNumber) {
      filtered = filtered.filter(u =>
        (u.phoneNumber || '').toLowerCase().includes(filters.phoneNumber.toLowerCase())
      );
    }

    setFilteredUsers(filtered);
    setTotalPages(Math.ceil(filtered.length / rowsPerPage));
    setCurrentPage(0);
  };

  useEffect(() => {
    applyFilters(users);
  }, [filters, rowsPerPage]);

  const handleFilter = (appliedFilters: Record<string, string>) => {
    setFilters({
      role: appliedFilters['role'] || '',
      phoneNumber: appliedFilters['phoneNumber'] || '',
    });
  };

  const handleDelete = (id: number) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    
    try {
      await apiService.delete(apiRoutes.users.delete(deleteId));
      const updatedUsers = users.filter(u => u.id !== deleteId);
      setUsers(updatedUsers);
      applyFilters(updatedUsers);
      setConfirmOpen(false);
      setDeleteId(null);
      window.dispatchEvent(new CustomEvent('show-toast', { 
        detail: { message: 'User deleted successfully', type: 'success' } 
      }));
    } catch (error) {
      console.error('Error deleting user:', error);
      window.dispatchEvent(new CustomEvent('show-toast', { 
        detail: { message: 'Failed to delete user', type: 'error' } 
      }));
    }
  };

  const confirmResetAuthenticator = async () => {
    if (!resetUser || resetting) return;
    setResetting(true);
    try {
      await apiService.post(apiRoutes.users.resetTwoFactor(resetUser.id), {});
      setUsers((prev) => prev.map((user) => (
        user.id === resetUser.id ? { ...user, twoFactorEnabled: false } : user
      )));
      setFilteredUsers((prev) => prev.map((user) => (
        user.id === resetUser.id ? { ...user, twoFactorEnabled: false } : user
      )));
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { message: `Two-way authentication reset for ${resetUser.userName}`, type: 'success' },
      }));
      setResetUser(null);
    } catch (error: any) {
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { message: error?.message || error?.error || 'Could not reset two-way authentication', type: 'error' },
      }));
    } finally {
      setResetting(false);
    }
  };

  const handleCreate = () => {
    setEditingUserId(null);
    setEditingUser(null);
    setDialogOpen(true);
  };

  const handleEdit = (id: number) => {
    setEditingUserId(id);
    setEditingUser(users.find((u) => u.id === id) ?? null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUserId(null);
    setEditingUser(null);
    fetchUsers();
  };

  return (
    <div className="p-4 w-full mx-auto bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen">
      <div className="bg-white rounded-lg shadow-md border border-slate-200 overflow-hidden mb-4">
      <CustomTable<User>
        variant="gameList"
        data={filteredUsers.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage)}
        title="Users"
        columns={[
          { key: 'firstName', label: 'First Name', filterable: true },
          { key: 'lastName', label: 'Last Name', filterable: true },
          { key: 'userName', label: 'UserName', filterable: true },
          {
            key: 'role',
            label: 'Role',
            filterable: true,
            filterType: 'text',
            render: (item: User) => item.role || '—',
          },
          {
            key: 'partyCode',
            label: 'Party',
            filterable: true,
            filterType: 'text',
            render: (item: User) => {
              if (!item.partyId && !item.partyCode) return '—';
              if (item.partyCode && item.partyName) return `${item.partyCode} - ${item.partyName}`;
              return item.partyCode || item.partyName || String(item.partyId);
            },
          },
          {
            key: 'modifiedOn',
            label: 'Last Login',
            render: (item: User) => (item.modifiedOn ? formatDateTimeIST(item.modifiedOn) : '—'),
          },
          {
            key: 'twoFactorEnabled',
            label: 'Authenticator',
            render: (item: User) => (
              <div className="flex items-center gap-2">
                <span className={item.twoFactorEnabled ? 'text-green-700 font-medium' : 'text-slate-400'}>
                  {item.twoFactorEnabled ? 'On' : 'Off'}
                </span>
                {canEditUser && item.twoFactorEnabled && (hasRole(Role.ADMIN) || item.role !== Role.ADMIN) && (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setResetUser(item);
                    }}
                    className="rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 hover:bg-amber-100"
                  >
                    Reset
                  </button>
                )}
              </div>
            ),
          },
        ]}
        headerButtons={canCreateUser ? [
          { label: 'Create New', onClick: handleCreate },
        ] : []}
        totalPages={totalPages}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={setRowsPerPage}
        loading={loading}
        onEdit={canEditUser ? handleEdit : undefined}
        onDelete={canDeleteUser ? handleDelete : undefined}
        getId={(user: User) => user.id}
        onFilter={handleFilter}
      />
      </div>

      {/* Create/Edit Side Drawer */}
      <CreateUserDialog
        isOpen={dialogOpen}
        userId={editingUserId}
        initialUser={editingUser}
        onClose={handleCloseDialog}
      /> 

      {/* Delete Confirmation */}
      <ConfirmationDialog
        isOpen={confirmOpen}
        title="Delete Confirmation"
        message="Are you sure you want to delete this user?"
        onConfirm={confirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />

      <ConfirmationDialog
        isOpen={resetUser !== null}
        title="Reset two-way authentication"
        message={resetUser
          ? `Reset two-way authentication for ${resetUser.userName}? They will sign in with username and password only. The linked phone stops working. They can turn it on again as a new setup.`
          : ''}
        onConfirm={() => { void confirmResetAuthenticator(); }}
        onCancel={() => { if (!resetting) setResetUser(null); }}
      />
    </div>
  );
};

export default Users;
